import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Separator } from 'components/ui/separator';
import { Checkbox } from 'components/ui/checkbox';
import { Logo } from 'components/Logo';
import { useAuth } from 'contexts/AuthContext';
import { useLanguage } from 'lib/i18n';
import { LanguageSwitcher } from 'components/LanguageSwitcher';
import { toast } from 'sonner';
import { getApiErrorMessage } from 'lib/api';
import { Mail, Lock, User, Phone, Loader2, Package, Plane, Truck, MapPin, Home } from 'lucide-react';

const AUTH_IMAGES = {
  side: '/images/courier-handover.jpg',
  mobile: '/images/international-delivery.jpg',
};

const AUTH_FALLBACK =
  'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%221200%22 height=%22720%22 viewBox=%220 0 1200 720%22%3E%3Cdefs%3E%3ClinearGradient id=%22g%22 x1=%220%22 y1=%220%22 x2=%221%22 y2=%221%22%3E%3Cstop stop-color=%22%23f59e0b%22 offset=%220%22/%3E%3Cstop stop-color=%22%23fdba74%22 offset=%220.5%22/%3E%3Cstop stop-color=%22%23ffedd5%22 offset=%221%22/%3E%3C/linearGradient%3E%3C/defs%3E%3Crect width=%221200%22 height=%22720%22 fill=%22url(%23g)%22/%3E%3Ctext x=%2260%22 y=%22380%22 font-family=%22Arial%22 font-size=%2260%22 font-weight=%22700%22 fill=%22%231f2937%22%3EInscription ColiBuddy%3C/text%3E%3C/svg%3E';

const handleImageError = (event) => {
  event.currentTarget.onerror = null;
  event.currentTarget.src = AUTH_FALLBACK;
};

const ROLES = [
  { id: 'sender', label: 'auth.roleSender', desc: 'auth.roleSenderDesc', icon: Package },
  { id: 'traveler', label: 'auth.roleTraveler', desc: 'auth.roleTravelerDesc', icon: Plane },
  { id: 'deliverer', label: 'auth.roleDeliverer', desc: 'auth.roleDelivererDesc', icon: Truck },
];

export default function RegisterPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { register } = useAuth();
  const { t } = useLanguage();
  const [form, setForm] = useState({
    email: '', password: '', full_name: '', city: '', country: '', phone: '',
    roles: [params.get('role') || 'sender'],
  });
  const [loading, setLoading] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  useEffect(() => {
    const r = params.get('role');
    if (r) setForm(f => ({ ...f, roles: [r] }));
  }, [params]);

  const toggleRole = (role) => {
    setForm(f => {
      const has = f.roles.includes(role);
      if (has && f.roles.length === 1) return f; // prevent deselecting the only role
      return { ...f, roles: has ? f.roles.filter(r => r !== role) : [...f.roles, role] };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.roles.length) { toast.error(t('auth.chooseRole')); return; }
    if (form.password.length < 6) { toast.error(t('auth.passwordTooShort')); return; }
    if (!acceptedTerms) { toast.error(t('auth.acceptRequired')); return; }
    setLoading(true);
    try {
      await register({ ...form, accepted_terms: acceptedTerms });
      toast.success(t('auth.accountCreated'));
      navigate('/dashboard');
    } catch (err) {
      toast.error(getApiErrorMessage(err, t('auth.registerFailed')));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:block relative hero-bg">
        <img
          src={AUTH_IMAGES.side}
          alt={t('auth.deliveryHandoverAlt')}
          className="absolute inset-0 h-full w-full object-cover"
          loading="lazy"
          onError={handleImageError}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-background/90 via-background/70 to-background/20" />
        <div className="absolute inset-0 flex flex-col justify-between p-12">
          <Link to="/"><Logo /></Link>
          <div>
            <h2 className="text-3xl font-bold mb-3">{t('auth.join')}</h2>
            <p className="text-muted-foreground max-w-md">
              {t('auth.joinText')}
            </p>
          </div>
          <div className="text-xs text-muted-foreground">© 2026 ColiBuddy</div>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <nav className="mb-6 flex items-center justify-between" aria-label={t('shell.menu')}>
            <Link to="/" className="inline-flex h-9 items-center gap-1.5 rounded-md bg-primary px-3 text-sm font-medium text-primary-foreground shadow-sm transition-colors hover:bg-primary/90" data-testid="auth-home-link">
              <Home size={16} /> {t('landing.home')}
            </Link>
            <LanguageSwitcher />
          </nav>
          <div className="lg:hidden mb-5 overflow-hidden rounded-xl border shadow-sm-custom">
            <img
              src={AUTH_IMAGES.mobile}
              alt="Professionnel présentant un colis"
              className="h-36 w-full object-cover"
              loading="lazy"
              onError={handleImageError}
            />
          </div>
          <h1 className="text-3xl font-bold mb-2">{t('auth.register')}</h1>
          <p className="text-muted-foreground mb-6">{t('auth.free')}</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>{t('auth.iAm')}</Label>
              <div className="grid grid-cols-1 gap-2 mt-1">
                {ROLES.map(r => {
                  const Icon = r.icon;
                  const active = form.roles.includes(r.id);
                  return (
                    <button key={r.id} type="button" onClick={() => toggleRole(r.id)} data-testid={`role-${r.id}-button`}
                      className={`flex items-center gap-3 p-3 rounded-lg border text-left transition-colors ${
                        active ? 'border-primary bg-primary/5' : 'border-border hover:bg-secondary'
                      }`}
                    >
                      <div className={`flex h-9 w-9 items-center justify-center rounded-md ${active ? 'bg-primary text-primary-foreground' : 'bg-secondary'}`}>
                        <Icon size={16} />
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-sm">{t(r.label)}</div>
                        <div className="text-xs text-muted-foreground">{t(r.desc)}</div>
                      </div>
                      <div className={`h-4 w-4 rounded border flex items-center justify-center ${active ? 'bg-primary border-primary' : 'border-border'}`}>
                        {active && <span className="h-2 w-2 rounded-sm bg-primary-foreground" />}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <Label htmlFor="full_name">{t('auth.fullName')}</Label>
              <div className="relative">
                <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input id="full_name" required value={form.full_name} onChange={e => setForm({...form, full_name: e.target.value})} className="pl-9" data-testid="register-name-input" />
              </div>
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input id="email" type="email" required value={form.email} onChange={e => setForm({...form, email: e.target.value.toLowerCase()})} className="pl-9" data-testid="register-email-input" />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="city">{t('auth.residenceCity')}</Label>
                <div className="relative">
                  <MapPin size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="city"
                    required
                    value={form.city}
                    onChange={e => setForm({ ...form, city: e.target.value })}
                    className="pl-9"
                    data-testid="register-city-input"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="country">{t('auth.residenceCountry')}</Label>
                <div className="relative">
                  <MapPin size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="country"
                    required
                    value={form.country}
                    onChange={e => setForm({ ...form, country: e.target.value })}
                    className="pl-9"
                    data-testid="register-country-input"
                  />
                </div>
              </div>
            </div>
            <div>
              <Label htmlFor="phone">{t('auth.phoneOptional')}</Label>
              <div className="relative">
                <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input id="phone" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="pl-9" data-testid="register-phone-input" />
              </div>
            </div>
            <div>
              <Label htmlFor="password">{t('auth.passwordMin')}</Label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input id="password" type="password" required minLength={6} value={form.password} onChange={e => setForm({...form, password: e.target.value})} className="pl-9" data-testid="register-password-input" />
              </div>
            </div>

            <div className="flex items-start gap-2">
              <Checkbox id="accepted_terms" checked={acceptedTerms} onCheckedChange={setAcceptedTerms} data-testid="register-terms-checkbox" />
              <Label htmlFor="accepted_terms" className="cursor-pointer text-sm font-normal leading-tight">
                {t('auth.acceptTerms')} <Link to="/terms" target="_blank" className="text-primary hover:underline">{t('auth.terms')}</Link> {t('auth.and')} <Link to="/terms#privacy" target="_blank" className="text-primary hover:underline">{t('auth.privacyPolicy')}</Link>
              </Label>
            </div>

            <Button type="submit" disabled={loading} className="w-full" data-testid="register-submit-button">
              {loading ? <Loader2 className="animate-spin" size={16} /> : t('auth.createMyAccount')}
            </Button>
          </form>

          <p className="text-center text-sm text-muted-foreground mt-6">
            {t('auth.alreadyRegistered')}{' '}
            <Link to="/login" className="text-primary font-medium hover:underline" data-testid="to-login-link">{t('auth.loginButton')}</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
